<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '480px' }"
    :header="dialogHeader"
    class="channel-selection-dialog"
  >
    <div class="channel-selection">
      <p class="channel-selection__description">
        {{ t('myLists.actions.unsubscribe.description') }}
      </p>

      <div class="channel-selection__options">
        <div class="channel-grid">
          <!-- Email channel -->
          <div
            class="channel-card"
            :class="{ 'channel-card--selected': channels.email }"
            @click="toggleChannel('email')"
          >
            <div class="channel-card__icon">
              <i class="fa-solid fa-envelope" />
            </div>
            <div class="channel-card__content">
              <span class="channel-card__label">{{ t('myLists.actions.channels.email') }}</span>
            </div>
            <transition name="fade">
              <div
                v-if="channels.email"
                class="channel-card__check"
              >
                <i class="fa-solid fa-check-circle" />
              </div>
            </transition>
          </div>

          <!-- SMS channel -->
          <div
            class="channel-card"
            :class="{ 'channel-card--selected': channels.sms }"
            @click="toggleChannel('sms')"
          >
            <div class="channel-card__icon">
              <i class="fa-solid fa-mobile-screen-button" />
            </div>
            <div class="channel-card__content">
              <span class="channel-card__label">{{ t('myLists.actions.channels.sms') }}</span>
            </div>
            <transition name="fade">
              <div
                v-if="channels.sms"
                class="channel-card__check"
              >
                <i class="fa-solid fa-check-circle" />
              </div>
            </transition>
          </div>

          <!-- WhatsApp channel -->
          <div
            class="channel-card"
            :class="{ 'channel-card--selected': channels.whatsapp }"
            @click="toggleChannel('whatsapp')"
          >
            <div class="channel-card__icon">
              <i class="fa-brands fa-whatsapp" />
            </div>
            <div class="channel-card__content">
              <span class="channel-card__label">{{ t('myLists.actions.channels.whatsapp') }}</span>
            </div>
            <transition name="fade">
              <div
                v-if="channels.whatsapp"
                class="channel-card__check"
              >
                <i class="fa-solid fa-check-circle" />
              </div>
            </transition>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <SpmButton
        :label="t('common.cancel')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('validate')"
        icon="pi pi-check"
        class-style="p-button p-button-danger"
        :disabled="!hasSelection"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import {
  defineComponent, ref, computed, reactive,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import type { ActionPayload, UnsubscribeResult, SubscriptionChannels } from './types';

export default defineComponent({
  name: 'UnsubscribeAction',

  components: {
    Dialog,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const currentPayload = ref<ActionPayload | null>(null);

    // Channel selection state
    const channels = reactive<SubscriptionChannels>({
      email: false,
      sms: false,
      whatsapp: false,
    });

    // Reset channels to default state
    const resetChannels = () => {
      channels.email = false;
      channels.sms = false;
      channels.whatsapp = false;
    };

    // Check if at least one channel is selected
    const hasSelection = computed(() => channels.email || channels.sms || channels.whatsapp);

    // Computed header based on context
    const dialogHeader = computed(() => {
      if (currentPayload.value?.context === 'grouped') {
        return t('myLists.actions.unsubscribe.headerGrouped');
      }
      return t('myLists.actions.unsubscribe.headerRow');
    });

    /**
     * Toggle a channel selection
     */
    const toggleChannel = (channel: keyof SubscriptionChannels) => {
      channels[channel] = !channels[channel];
    };

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: ActionPayload) => {
      resetChannels();
      currentPayload.value = payload;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentPayload.value = null;
      resetChannels();
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = () => {
      if (!currentPayload.value || !hasSelection.value) return;

      const result: UnsubscribeResult = {
        context: currentPayload.value.context,
        channels: { ...channels },
        selectionInfo: currentPayload.value.selectionInfo,
        rowId: currentPayload.value.rowId,
      };

      emit('confirm', result);
      close();
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      dialogHeader,
      channels,
      hasSelection,
      toggleChannel,
      close,
      confirm,
    };
  },
});
</script>

<style scoped lang="scss">
.channel-selection {
  padding: 0.5rem 0;

  &__description {
    margin: 0 0 1.5rem;
    color: #64748b;
    font-size: 0.95rem;
    line-height: 1.5;
    text-align: center;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 1.5rem;
  }

  &__separator {
    display: flex;
    align-items: center;
    text-align: center;
    color: #94a3b8;
    font-size: 0.85rem;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.05em;

    &::before,
    &::after {
      content: '';
      flex: 1;
      border-bottom: 1px solid #e2e8f0;
    }

    &::before {
      margin-right: 1rem;
    }

    &::after {
      margin-left: 1rem;
    }
  }
}

.channel-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.channel-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.25rem 0.5rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  height: 110px;

  &:hover:not(.channel-card--disabled) {
    border-color: #cbd5e1;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
    transform: translateY(-2px);
  }

  &--selected {
    background: #fef2f2 !important;
    border-color: #ef4444 !important;
    box-shadow: 0 4px 12px rgba(239, 68, 68, 0.15) !important;

    .channel-card__icon {
      color: #ef4444;
    }

    .channel-card__label {
      color: #b91c1c;
      font-weight: 600;
    }
  }

  &--disabled {
    opacity: 0.4;
    cursor: not-allowed;
    filter: grayscale(1);
  }

  &__icon {
    font-size: 1.75rem;
    color: #94a3b8;
    margin-bottom: 0.75rem;
    transition: color 0.2s ease;
  }

  &__label {
    font-size: 0.875rem;
    color: #475569;
    font-weight: 500;
    transition: color 0.2s ease;
  }

  &__check {
    position: absolute;
    top: 8px;
    right: 8px;
    color: #ef4444;
    font-size: 1rem;
  }
}

.channel-row {
  display: flex;
  align-items: center;
  padding: 1rem 1.25rem;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #cbd5e1;
    background: #f8fafc;
  }

  &--selected {
    background: #fee2e2 !important;
    border-color: #dc2626 !important;

    .channel-row__icon {
      background: #dc2626;
      color: #fff;
      border-color: #dc2626;
    }

    .custom-checkbox {
      background: #dc2626;
      border-color: #dc2626;
      color: #fff;
    }
  }

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    background: #f1f5f9;
    color: #64748b;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.25rem;
    margin-right: 1rem;
    border: 1px solid #e2e8f0;
    transition: all 0.2s ease;
  }

  &__content {
    flex: 1;
    display: flex;
    flex-direction: column;
  }

  &__label {
    font-weight: 600;
    color: #1e293b;
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }

  &__hint {
    font-size: 0.75rem;
    color: #64748b;
  }

  &__action {
    margin-left: 1rem;
  }
}

.custom-checkbox {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  border: 2px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  color: transparent;
  transition: all 0.2s ease;
  background: #fff;
}

// Transitions
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
